import * as React from "react";
import { shallowEqual } from "react-redux";

import type { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { useAppSelector } from "@/store/hooks";
import {
  businessReportsApi,
  useGetContractFamilyProjectionGridRowsQuery,
  useGetSnapshotGridRowsQuery,
  useLazyGetContractFamilyProjectionGridRowByResultQuery,
  useLazyGetSnapshotGridRowByResultQuery,
  type ContractFamilyProjectionGridQueryResponse,
  type SnapshotGridField,
} from "@/modules/snapshots/businessReports.api";
import { useGetContractFamilySummaryGridQuery } from "@/api/contract-family-pipeline";
import type { SnapshotGridQueryPayload } from "@/modules/snapshots/grid/gridQueryPayload";
import { useGetSnapshotViewGridRowsQuery } from "@/modules/snapshots/snapshotViews.api";
import { snapshotViewGridPayload, type SnapshotViewConfiguration } from "@/modules/snapshots/snapshotViews.types";
import type { PipelineSummaryGridGroup, PipelineSummaryGridQueryPayload } from "./pipelinePreview.types";
import { adaptPipelineSummaryGridPage, adaptSnapshotOutcomeRow, type SnapshotGridRowModel } from "./rowModel";

export interface SnapshotGridCapabilities {
  chrome: boolean;
  analyse: boolean;
  columnAnalysis: boolean;
  export: boolean;
  subcollections: boolean;
  editRows: boolean;
  deleteRows: boolean;
  aiColumns: boolean;
  dashboard: boolean;
  sharing: boolean;
  actionsMenu: boolean;
  pivot: boolean;
  bulkSelection: boolean;
  reviewTriage: boolean;
}

interface SnapshotGridDataSourceBase {
  mode: "collection" | "contract-family-projection" | "pipeline-preview" | "view";
  payload: SnapshotGridQueryPayload;
  capabilities?: SnapshotGridCapabilities;
}

interface PipelinePreviewDataSourceInput extends SnapshotGridDataSourceBase {
  mode: "pipeline-preview";
  pipelineRunId: string;
  snapshotCollectionId?: never;
  snapshotViewId?: never;
  snapshotViewConfiguration?: never;
}

interface CollectionDataSourceInput extends SnapshotGridDataSourceBase {
  mode: "collection" | "contract-family-projection" | "view";
  snapshotCollectionId: string;
  snapshotViewId?: string;
  snapshotViewConfiguration?: SnapshotViewConfiguration;
  pipelineRunId?: never;
}

export type SnapshotGridDataSourceInput = PipelinePreviewDataSourceInput | CollectionDataSourceInput;

export interface SnapshotGridDataSourcePage {
  rows: SnapshotGridRowModel[];
  fields: SnapshotGridField[];
  pagination: {
    unit: "row" | "family";
    offset: number;
    limit: number;
    total: number;
  };
  sourceCollectionId: string;
  templateId: string;
  pipelineGroups?: PipelineSummaryGridGroup[];
  filteredOutcomeIds?: string[];
  projectionRows?: ContractFamilyProjectionGridQueryResponse["projection_rows"];
  issueTotal: number;
  issues: import("./rowModel").SnapshotGridCellIssue[];
}

const COLLECTION_CAPABILITIES: SnapshotGridCapabilities = {
  chrome: true,
  analyse: true,
  columnAnalysis: true,
  export: true,
  subcollections: true,
  editRows: true,
  deleteRows: true,
  aiColumns: true,
  dashboard: true,
  sharing: true,
  actionsMenu: true,
  pivot: true,
  bulkSelection: true,
  reviewTriage: true,
};

const VIEW_CAPABILITIES: SnapshotGridCapabilities = {
  ...COLLECTION_CAPABILITIES,
  reviewTriage: false,
};

const CONTRACT_FAMILY_CAPABILITIES: SnapshotGridCapabilities = {
  chrome: true,
  analyse: true,
  columnAnalysis: true,
  export: true,
  subcollections: true,
  editRows: false,
  deleteRows: false,
  aiColumns: false,
  dashboard: false,
  sharing: true,
  actionsMenu: true,
  pivot: true,
  bulkSelection: true,
  reviewTriage: false,
};

export const PIPELINE_PREVIEW_CAPABILITIES: SnapshotGridCapabilities = {
  chrome: false,
  analyse: false,
  columnAnalysis: false,
  export: false,
  subcollections: false,
  editRows: false,
  deleteRows: false,
  aiColumns: false,
  dashboard: false,
  sharing: false,
  actionsMenu: false,
  pivot: false,
  bulkSelection: false,
  reviewTriage: false,
};

export const shouldRefetchSnapshotGrid = (previousSignal: number | undefined, nextSignal: number | undefined) =>
  nextSignal !== undefined && previousSignal !== nextSignal;

export const getSnapshotGridCapabilities = (mode: SnapshotGridDataSourceInput["mode"]): SnapshotGridCapabilities =>
  mode === "pipeline-preview"
    ? PIPELINE_PREVIEW_CAPABILITIES
    : mode === "contract-family-projection"
      ? CONTRACT_FAMILY_CAPABILITIES
      : mode === "view"
        ? VIEW_CAPABILITIES
        : COLLECTION_CAPABILITIES;

export const useSnapshotGridDataSource = ({
  mode,
  snapshotCollectionId = "",
  snapshotViewId,
  snapshotViewConfiguration,
  pipelineRunId,
  payload,
  capabilities,
}: SnapshotGridDataSourceInput) => {
  const isProjection = mode === "contract-family-projection";
  const isView = mode === "view";
  const isPipelinePreview = mode === "pipeline-preview";
  const pipelinePayload = React.useMemo<PipelineSummaryGridQueryPayload>(
    () => ({
      offset: payload.offset,
      limit: 25,
      query: payload.query,
      ordering: payload.ordering,
      column_filters: payload.column_filters as PipelineSummaryGridQueryPayload["column_filters"],
    }),
    [payload.column_filters, payload.offset, payload.ordering, payload.query],
  );
  const collectionQuery = useGetSnapshotGridRowsQuery(
    { snapshotCollectionId, payload },
    { skip: !snapshotCollectionId || isProjection || isView || isPipelinePreview, refetchOnMountOrArgChange: true },
  );
  const viewQuery = useGetSnapshotViewGridRowsQuery(
    {
      snapshotViewId: snapshotViewId ?? "",
      payload: snapshotViewGridPayload(
        payload,
        snapshotViewConfiguration ?? {
          schema_version: 1,
          column_order: [],
          hidden_column_keys: [],
          column_filters: {},
          date_range: null,
          ordering: payload.ordering,
        },
      ),
    },
    {
      skip: !snapshotViewId || !snapshotViewConfiguration || !isView || isPipelinePreview,
      refetchOnMountOrArgChange: true,
    },
  );
  const projectionQuery = useGetContractFamilyProjectionGridRowsQuery(
    { snapshotCollectionId, payload },
    { skip: !snapshotCollectionId || !isProjection || isPipelinePreview, refetchOnMountOrArgChange: true },
  );
  const pipelineQuery = useGetContractFamilySummaryGridQuery(
    { runId: pipelineRunId ?? "", payload: pipelinePayload },
    { skip: !isPipelinePreview || !pipelineRunId, refetchOnMountOrArgChange: true },
  );
  const page = React.useMemo<SnapshotGridDataSourcePage | undefined>(() => {
    if (isPipelinePreview) {
      if (!pipelineQuery.currentData) return undefined;
      const adapted = adaptPipelineSummaryGridPage(pipelineQuery.currentData);
      return {
        ...adapted,
        fields: adapted.fields,
        pipelineGroups: pipelineQuery.currentData.groups,
      };
    }

    const currentData = isView
      ? viewQuery.currentData
      : isProjection
        ? projectionQuery.currentData
        : collectionQuery.currentData;
    if (!currentData) return undefined;

    return {
      rows: currentData.results.map(adaptSnapshotOutcomeRow),
      fields: currentData.fields,
      pagination: {
        unit: "row",
        offset: currentData.offset,
        limit: currentData.limit,
        total: currentData.total,
      },
      sourceCollectionId: snapshotCollectionId,
      templateId: "",
      filteredOutcomeIds: currentData.filtered_outcome_ids,
      projectionRows: isProjection
        ? (currentData as ContractFamilyProjectionGridQueryResponse).projection_rows
        : undefined,
      issueTotal: 0,
      issues: [],
    };
  }, [
    collectionQuery.currentData,
    isPipelinePreview,
    isProjection,
    isView,
    pipelineQuery.currentData,
    projectionQuery.currentData,
    snapshotCollectionId,
    viewQuery.currentData,
  ]);

  const query = isPipelinePreview
    ? pipelineQuery
    : isView
      ? viewQuery
      : isProjection
        ? projectionQuery
        : collectionQuery;

  return {
    mode,
    isProjection,
    isPipelinePreview,
    query,
    page,
    capabilities: capabilities ?? getSnapshotGridCapabilities(mode),
  } as const;
};

const noRefreshedRows: BusinessReportOutcome[] = [];

/**
 * Every row of this collection fetched on its own through the `grid-result` endpoints (a cell edit
 * refreshing its row, a citation click). The grid merges them into the rows it already holds, so a
 * row on an earlier page is updated without re-querying any page. Stable while no fetch lands.
 */
export const useRefreshedSnapshotGridRows = (
  snapshotCollectionId: string,
  isProjection: boolean,
): BusinessReportOutcome[] =>
  useAppSelector(state => {
    if (!snapshotCollectionId) return noRefreshedRows;
    const endpointName = isProjection ? "getContractFamilyProjectionGridRowByResult" : "getSnapshotGridRowByResult";
    const rows = businessReportsApi.util
      .selectCachedArgsForQuery(state, endpointName)
      .filter(args => args.snapshotCollectionId === snapshotCollectionId)
      .flatMap(args => {
        const { data } = businessReportsApi.endpoints[endpointName].select(args)(state);
        return data ? [data] : [];
      });
    return rows.length === 0 ? noRefreshedRows : rows;
  }, shallowEqual);

export const useSnapshotGridRowFetch = (snapshotCollectionId: string, isProjection: boolean) => {
  const [fetchCollectionRowByResult] = useLazyGetSnapshotGridRowByResultQuery();
  const [fetchProjectionRowByResult] = useLazyGetContractFamilyProjectionGridRowByResultQuery();

  return React.useCallback(
    (resultId: string) =>
      (isProjection ? fetchProjectionRowByResult : fetchCollectionRowByResult)({
        snapshotCollectionId,
        resultId,
      }).unwrap(),
    [fetchCollectionRowByResult, fetchProjectionRowByResult, isProjection, snapshotCollectionId],
  );
};
